import { Button, DatePicker, Input, Select, Space } from "antd";
import { TableProps } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import AddIcon from "../../../assets/icons/AddIcon";
import DateIcon from "../../../assets/icons/DateIcon";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SortIcon from "../../../assets/icons/SortIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import AddExpanseModal from "../../../components/Expanse/AddExpanseModal";
import ReportRequestModalWithMonth from "../../../components/Report/ReportRequestModalWithMonth";
import CustomTable from "../../../components/ui/CustomTable";
import { useGetDepartmentsQuery } from "../../../features/department/departmentApi";
import { useExpenseEmployeeQuery } from "../../../features/expense/expenseApi";
import {
  useGetExpenseDetailsReportMutation,
  useGetExpenseReportMutation,
} from "../../../features/report/reportApi";
import useDebounce from "../../../hooks/useDebounce";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { GetExpenseParams, GetExpenseResponseType } from "../../../types";
import {
  addCommaInNumber,
  countParPageSerialNumber,
} from "../../../utils/number";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
  skipData,
  underscoreToCapitalizeString,
} from "../../../utils/utils";

const searchTypeList = ["name", "employee_id"] as const;

const searchTypeSelect = searchTypeList?.map((item) => ({
  label: underscoreToCapitalizeString(item),
  value: item,
}));

type ModalType = "REPORT_REQUEST_DETAILED" | "REPORT_REQUEST" | "ADD_EXPANSE";

export default function Expense() {
  const location = useLocation();
  const { keyword, handleInputChange } = useDebounce();
  const [month, setMonth] = useState<Dayjs | null>(dayjs());
  const { authInfo } = useAppSelector((state) => state.auth);
  const [departmentId, setDepartmentId] = useState<number | undefined>(
    undefined
  );
  const { open, handleClose, handleOpen } = useModal<undefined, ModalType>();
  const { page, limit, handlePagination, handleLimit, resetPagination } =
    useTable();
  const [searchType, setSearchType] =
    useState<(typeof searchTypeList)[number]>("name");

  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });

  const monthParams = month ? { month: dayjs(month).format("YYYY-MM") } : {};
  const expanseParams: Omit<GetExpenseParams, "skip" | "limit"> = {
    department_id: departmentId,
    search: keyword,
    ...(keyword ? { search_type: searchType } : {}),
    ...monthParams,
    manager_id: authInfo?.role === "Manager" ? authInfo?.id : undefined,
  };
  const { isLoading, isError, error, data } = useExpenseEmployeeQuery({
    params: {
      limit,
      skip: skipData(page, limit),
      ...expanseParams,
    },
  });
  const [
    getExpenseReport,
    {
      isLoading: isGenerateLoading,
      isError: isGenerateError,
      error: generateError,
      isSuccess: isGenerateSuccess,
    },
  ] = useGetExpenseReportMutation();
  const [
    getExpenseDetailsReport,
    {
      isLoading: isGenerateDetailedLoading,
      isError: isGenerateDetailedError,
      error: generateDetailedError,
      isSuccess: isGenerateDetailedSuccess,
    },
  ] = useGetExpenseDetailsReportMutation();
  useError(isGenerateError, generateError, "Expense report request fail");
  useError(
    isGenerateDetailedError,
    generateDetailedError,
    "Expense detailed report request fail"
  );
  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isError, error, "Get expense employee data fail");

  const topBarData = data?.data?.dashboard;

  const columns: TableProps<
    GetExpenseResponseType["data"]["expenses"][0]
  >["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Month",
      dataIndex: "month",
      render: (val) => val || "n/a",
    },
    {
      title: "Employee ID",
      dataIndex: "employee_id",
      sorter: (a, b) =>
        Number(a?.employee_id ?? 0) - Number(b?.employee_id ?? 0),
      sortIcon: () => <SortIcon />,
      render: (val) => val ?? "n/a",
    },
    {
      title: <span className="text-nowrap mr-1">Employee Name</span>,
      dataIndex: "employee_name",
      sorter: (a, b) => a?.employee_name?.localeCompare(b?.employee_name),
      sortIcon: () => <SortIcon />,
    },
    // {
    //   title: "ID",
    //   dataIndex: "business_member_id",
    // },
    {
      title: "Department",
      dataIndex: "department",
    },
    {
      title: "Transport (BDT)",
      dataIndex: "transport",
      // align: "center",
    },
    {
      title: "Food (BDT)",
      dataIndex: "food",
      // align: "center",
    },
    {
      title: "Others (BDT)",
      dataIndex: "others",
      // align: "center",
    },
    {
      title: "Amount (BDT)",
      dataIndex: "total_amount",
      // align: "center",
      sorter: (a, b) => (a?.total_amount || 0) - (b?.total_amount || 0),
      sortIcon: () => <SortIcon />,
    },
    {
      title: "Action",
      dataIndex: "business_member_id",
      render: (val) => (
        <Link
          to={`${location.pathname}/view/${val}?date=${dayjs(month).format(
            "YYYY-MM"
          )}`}
        >
          <Button className="action-button" size="small" icon={<ViewIcon />} />
        </Link>
      ),
    },
  ];

  const handleDownloadRequest = (dates: {
    start_date: string;
    end_date: string;
  }) => {
    switch (open?.type) {
      case "REPORT_REQUEST_DETAILED":
        getExpenseDetailsReport({
          body: {
            name: "Expense report details",
          },
          params: { ...expanseParams, month: undefined, ...dates },
        });
        break;
      case "REPORT_REQUEST":
        getExpenseReport({
          body: {
            name: "Expense report",
          },
          params: { ...expanseParams, month: undefined, ...dates },
        });
        break;
    }
  };

  useEffect(() => {
    if (keyword) resetPagination();
  }, [keyword, resetPagination]);

  return (
    <SidebarLayout title="Expense">
      {open.type !== "ADD_EXPANSE" && (
        <ReportRequestModalWithMonth
          handleClose={handleClose}
          open={open?.open}
          handleSubmit={handleDownloadRequest}
          isLoading={isGenerateLoading || isGenerateDetailedLoading}
          isSuccess={isGenerateSuccess || isGenerateDetailedSuccess}
        />
      )}

      {open.type === "ADD_EXPANSE" && (
        <AddExpanseModal handleClose={handleClose} open={open?.open} />
      )}

      <div className="bg-white p-4 mb-6 rounded-md border border-extra-light">
        {authInfo?.role === "Admin" ? (
          <div className="flex items-center justify-between gap-4 mb-4 pb-4 w-full border-b border-extra-light">
            <Space wrap size={10}>
              {/* <Button
              icon={<DownloadIcon />}
              className="download-button"
              onClick={handleExpenseDetailsExcel}
              loading={isExpenseDetailsExcelLoading}
            >
              Download Detailed Expense Report
            </Button>
            <Button
              icon={<DownloadIcon />}
              className="download-button"
              onClick={handleExpenseExcel}
              loading={isExpenseExcelLoading}
            >
              Download Expense Report
            </Button> */}

              <Button
                onClick={() => handleOpen({ type: "REPORT_REQUEST_DETAILED" })}
                className="download-button w-full"
                icon={<DownloadIcon />}
              >
                Detailed Expense Report Request
              </Button>
              <Button
                onClick={() => handleOpen({ type: "REPORT_REQUEST" })}
                className="download-button w-full"
                icon={<DownloadIcon />}
              >
                Expense Report Request
              </Button>
            </Space>

            <Button
              onClick={() => handleOpen({ type: "ADD_EXPANSE" })}
              type="primary"
              className="!px-[15px]"
              icon={<AddIcon />}
            >
              Add Expense
            </Button>
          </div>
        ) : null}

        <div className="w-full flex items-center gap-4 flex-wrap">
          <h6 className="text-header text-base font-medium pr-2.5">Total: </h6>
          <div className="space-y-4">
            <Space
              align="center"
              wrap
              size={10}
              className="w-full flex-1 min-h-10 px-3 py-1.5 text-[#CB5B2A] bg-[#FFD3C0] rounded-md justify-between text-base font-medium flex-wrap"
            >
              <span>
                Employee: {addCommaInNumber(topBarData?.totalEmployees ?? 0)}
              </span>
              <span>
                Department:{" "}
                {addCommaInNumber(topBarData?.totalDepartments ?? 0)}
              </span>
              <span>
                Transport: BDT{" "}
                {addCommaInNumber(topBarData?.transportTotalBDT ?? 0)}
              </span>
              <span>
                Food: BDT {addCommaInNumber(topBarData?.foodTotalBDT ?? 0)}
              </span>
              <span>
                Others: BDT {addCommaInNumber(topBarData?.othersTotalBDT ?? 0)}
              </span>
              <span>
                Net Expense: BDT{" "}
                {addCommaInNumber(topBarData?.netExpenseTotalBDT ?? 0)}
              </span>
            </Space>
            <div className="w-full inline-flex items-center gap-4 flex-wrap">
              <Space
                align="center"
                wrap
                size={10}
                className="min-h-10 px-3 py-1.5 text-status-success-text bg-status-success-bg rounded-md justify-between text-base font-medium flex-wrap"
              >
                <span>
                  Approved: BDT{" "}
                  {addCommaInNumber(topBarData?.totalApprovedBDT ?? 0)}
                </span>
              </Space>
              <Space
                align="center"
                wrap
                size={10}
                className="min-h-10 px-3 py-1.5 text-status-warning-text bg-status-warning-bg rounded-md justify-between text-base font-medium flex-wrap"
              >
                <span>
                  Pending: BDT{" "}
                  {addCommaInNumber(topBarData?.totalPendingBDT ?? 0)}
                </span>
              </Space>
            </div>
          </div>
        </div>
      </div>

      <CustomTable
        loading={isLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.totalLength}
        dataSource={addKeyInArray(data?.data?.expenses)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <DatePicker
              format="MMMM YYYY"
              style={{ width: 160 }}
              suffixIcon={<DateIcon />}
              picker="month"
              onChange={(val) => {
                setMonth(val);
                resetPagination();
              }}
              value={month}
              className="clear-button-hide"
            />
            <Select
              showSearch
              placeholder="Search Type"
              value={searchType}
              options={searchTypeSelect}
              onChange={(val) => {
                setSearchType(val);
                resetPagination();
              }}
              filterOption={selectSearchOption}
              className="min-w-[100px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Input
              key={searchType}
              suffix={<SearchIcon />}
              placeholder={`Search by ${underscoreToCapitalizeString(
                searchType,
                true
              )}`}
              style={{ minWidth: 180 }}
              onChange={(val) => {
                handleInputChange(val);
              }}
            />

            <Select
              allowClear
              showSearch
              placeholder="Department"
              value={departmentId}
              filterOption={selectSearchOption}
              options={selectArrayOption(
                departmentData?.data?.departments || [],
                "name",
                "id"
              )}
              className="min-w-[130px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              onChange={(val) => {
                setDepartmentId(val);
                resetPagination();
              }}
            />
          </>
        }
      />
    </SidebarLayout>
  );
}
